import React, { useState, useEffect } from 'react';
import { CheckCircle, Star, Zap, CreditCard } from 'lucide-react';
import { subscriptionUtils } from '../services/subscriptionService.js';
import stripeSyncService from '../services/stripeSyncService';
import stripeProductsConfigService from '../services/stripeProductsConfigService';

/**
 * Composant StripeSubscriptionPlans - Affiche les plans d'abonnement depuis Stripe
 * Remplace le composant SubscriptionPlans avec des plans codés en dur
 */
const StripeSubscriptionPlans = ({ 
  employeeId, 
  onPlanSelect, 
  currentSubscription, 
  showOfferedPlan = false, 
  hasUsedFreeTrial = false 
}) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Charger les produits Stripe pour les abonnements
  useEffect(() => {
    loadStripeProducts();
  }, []);

  const loadStripeProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      // Récupérer tous les produits Stripe et la configuration
      const [allProducts, configuredProductIds] = await Promise.all([
        stripeSyncService.getProductsWithCache(),
        stripeProductsConfigService.getSubscriptionProductIds()
      ]);

      console.log('📦 Produits Stripe disponibles:', {
        total: allProducts.length,
        configuredIds: configuredProductIds
      });

      // Filtrer les produits configurés pour les abonnements
      // Priorité 1: Produits explicitement configurés dans la configuration
      // Priorité 2: Produits avec métadonnées correspondant aux abonnements
      const configuredProducts = allProducts.filter(product => {
        // Si le produit est explicitement configuré, l'inclure
        if (configuredProductIds.includes(product.id)) {
          console.log(`✅ Produit configuré trouvé: ${product.name} (${product.id})`);
          return true;
        }
        
        // Sinon, vérifier les métadonnées pour les abonnements
        const hasSubscriptionMetadata = 
          product.metadata?.category === 'subscription' && 
          product.metadata?.target === 'employee' &&
          product.active;
          
        if (hasSubscriptionMetadata) {
          console.log(`📋 Produit trouvé par métadonnées: ${product.name} (${product.id})`);
        }
        
        return hasSubscriptionMetadata;
      });

      // Log des produits non trouvés mais configurés
      const missingConfiguredProducts = configuredProductIds.filter(id => 
        !allProducts.some(product => product.id === id)
      );
      
      if (missingConfiguredProducts.length > 0) {
        console.warn('⚠️ Produits configurés mais non trouvés dans Stripe:', missingConfiguredProducts);
      }

      console.log('📊 Résultat du filtrage:', {
        totalProduits: allProducts.length,
        produitsConfigurés: configuredProducts.length,
        produitsManquants: missingConfiguredProducts.length
      });

      // Transformer les produits Stripe en format compatible avec l'interface existante
      const formattedPlans = configuredProducts.map(product => {
        // Trouver le prix principal (premier prix actif)
        const mainPrice = product.prices.find(price => price.active) || product.prices[0];
        
        // Déterminer le type de période
        const isMonthly = mainPrice?.recurring?.interval === 'month';
        const isYearly = mainPrice?.recurring?.interval === 'year';
        
        // Formater les données du plan
        return {
          id: product.id, // ID Stripe du produit
          stripePriceId: mainPrice?.id, // ID Stripe du prix
          name: product.name,
          price: mainPrice?.unit_amount || 0, // Prix en centimes
          period: isMonthly ? 'mois' : isYearly ? 'an' : 'ponctuel',
          description: product.description || 'Plan d\'abonnement premium',
          tagline: getTagline(product.metadata),
          features: getFeatures(product.metadata),
          isFree: false,
          isOffered: false,
          popular: product.metadata?.features === 'premium',
          color: getColor(product.metadata),
          badge: getBadge(product.metadata),
          metadata: product.metadata
        };
      });

      // Ajouter le plan gratuit (toujours disponible)
      const freePlan = {
        id: 'free',
        name: 'Découverte',
        price: 0,
        period: '30 jours',
        description: 'Testez toutes les fonctionnalités gratuitement pendant 30 jours',
        tagline: 'Essai gratuit',
        features: [
          'Accès complet à toutes les fonctionnalités',
          'Planning personnel (4 vues)',
          'Gestion de vos rendez-vous',
          'Pointage GPS et NFC',
          'Messagerie avec bénéficiaires et intervenants',
          'Conversations 1-to-1 et groupes',
          'Application mobile PWA',
          'Mode hors ligne',
          'Support par email (48h)',
          'Aucune carte bancaire requise'
        ],
        isFree: true,
        isOffered: false,
        popular: false,
        color: 'gray',
        badge: '30 jours gratuits'
      };

      // Ajouter le plan offert si demandé
      const allPlans = [freePlan, ...formattedPlans];
      if (showOfferedPlan) {
        allPlans.splice(1, 0, {
          id: 'offered',
          name: 'Premium Offert',
          price: 0,
          period: 'an',
          description: 'Accès complet aux fonctionnalités premium sans frais',
          tagline: 'Offre spéciale',
          features: [
            'Accès illimité à toutes les fonctionnalités',
            'Planning avancé (4 vues complètes)',
            'Gestion complète de vos bénéficiaires',
            'Rapports et statistiques détaillés',
            'Pointage GPS et NFC',
            'Messagerie complète avec bénéficiaires et intervenants',
            'Conversations 1-to-1 et groupes illimités',
            'Export de données illimité',
            'Support prioritaire (24h)',
            'Mode hors ligne complet'
          ],
          isFree: false,
          isOffered: true,
          popular: true,
          color: 'purple',
          badge: 'Valable 1 an'
        });
      }

      setPlans(allPlans);
    } catch (err) {
      console.error('Erreur lors du chargement des produits Stripe:', err);
      setError('Impossible de charger les plans d\'abonnement. Veuillez réessayer.');
      
      // Plan de secours avec les plans par défaut
      setPlans(getDefaultPlans(showOfferedPlan));
    } finally {
      setLoading(false);
    }
  };

  // Plans par défaut en cas d'erreur
  const getDefaultPlans = (showOffered) => {
    const defaultPlans = [
      {
        id: 'free',
        name: 'Découverte',
        price: 0,
        period: '30 jours',
        description: 'Testez toutes les fonctionnalités gratuitement pendant 30 jours',
        tagline: 'Essai gratuit',
        features: [
          'Accès complet à toutes les fonctionnalités',
          'Planning personnel (4 vues)',
          'Gestion de vos rendez-vous',
          'Pointage GPS et NFC',
          'Messagerie avec bénéficiaires et intervenants',
          'Conversations 1-to-1 et groupes',
          'Application mobile PWA',
          'Mode hors ligne',
          'Support par email (48h)',
          'Aucune carte bancaire requise'
        ],
        isFree: true,
        isOffered: false,
        popular: false,
        color: 'gray',
        badge: '30 jours gratuits'
      },
      {
        id: 'monthly',
        name: 'Essentiel',
        price: 1500,
        period: 'mois',
        description: 'Tout ce dont vous avez besoin pour travailler efficacement',
        tagline: 'Le plus populaire',
        features: [
          'Planning complet (4 vues : jour, semaine, mois, mobile)',
          'Gestion illimitée de vos bénéficiaires assignés',
          'Pointage GPS avec validation de position',
          'Pointage NFC pour sécuriser vos interventions',
          'Messagerie complète avec bénéficiaires et intervenants',
          'Conversations 1-to-1 et groupes illimités',
          'Rapports d\'activité personnalisés',
          'Notifications push intelligentes',
          'Export de vos données en PDF/Excel',
          'Mode hors ligne (synchronisation auto)',
          'Application PWA installable',
          'Support prioritaire par email (24h)'
        ],
        isFree: false,
        popular: true,
        color: 'blue',
        badge: 'Recommandé'
      },
      {
        id: 'yearly',
        name: 'Essentiel Annuel',
        price: 9600,
        period: 'an',
        description: 'L\'offre annuelle avec 4 mois offerts',
        tagline: 'Meilleure économie',
        features: [
          'Toutes les fonctionnalités du plan Essentiel',
          'Planning complet (4 vues optimisées)',
          'Gestion illimitée des bénéficiaires',
          'Pointage GPS et NFC avancés',
          'Messagerie complète avec bénéficiaires et intervenants',
          'Conversations 1-to-1 et groupes illimités',
          'Rapports et analytics personnalisés',
          'Mode hors ligne complet',
          'Export de données illimité',
          'Support prioritaire (24h)',
          'Facturation simplifiée (1 fois/an)',
          'Économie de 84€ sur l\'année',
          'Formation en ligne offerte'
        ],
        isFree: false,
        isOffered: false,
        popular: false,
        color: 'green',
        badge: '4 mois offerts',
        savings: '84€ d\'économie'
      }
    ];

    if (showOffered) {
      defaultPlans.splice(1, 0, {
        id: 'offered',
        name: 'Premium Offert',
        price: 0,
        period: 'an',
        description: 'Accès complet aux fonctionnalités premium sans frais',
        tagline: 'Offre spéciale',
        features: [
          'Accès illimité à toutes les fonctionnalités',
          'Planning avancé (4 vues complètes)',
          'Gestion complète de vos bénéficiaires',
          'Rapports et statistiques détaillés',
          'Pointage GPS et NFC',
          'Messagerie complète avec bénéficiaires et intervenants',
          'Conversations 1-to-1 et groupes illimités',
          'Export de données illimité',
          'Support prioritaire (24h)',
          'Mode hors ligne complet'
        ],
        isFree: false,
        isOffered: true,
        popular: true,
        color: 'purple',
        badge: 'Valable 1 an'
      });
    }

    return defaultPlans;
  };

  // Helper functions pour formater les données Stripe
  const getTagline = (metadata) => {
    if (metadata?.features === 'premium') return 'Premium';
    if (metadata?.features === 'enterprise') return 'Entreprise';
    return 'Essentiel';
  };

  const getFeatures = (metadata) => {
    const baseFeatures = [
      'Planning complet (4 vues)',
      'Gestion des bénéficiaires',
      'Pointage GPS et NFC',
      'Messagerie complète',
      'Mode hors ligne',
      'Support prioritaire'
    ];

    if (metadata?.features === 'premium') {
      return [
        ...baseFeatures,
        'Rapports avancés',
        'Analytics détaillés',
        'Export illimité',
        'Formation en ligne'
      ];
    }

    if (metadata?.features === 'enterprise') {
      return [
        ...baseFeatures,
        'Rapports personnalisés',
        'Support 24/7',
        'Formation personnalisée',
        'API avancée'
      ];
    }

    return baseFeatures;
  };

  const getColor = (metadata) => {
    switch (metadata?.features) {
      case 'premium': return 'purple';
      case 'enterprise': return 'orange';
      default: return 'blue';
    }
  };

  const getBadge = (metadata) => {
    switch (metadata?.features) {
      case 'premium': return 'Premium';
      case 'enterprise': return 'Entreprise';
      default: return 'Recommandé';
    }
  };

  const getCurrentPlanId = () => {
    if (!currentSubscription) return null;
    return currentSubscription.planId;
  };

  const isPlanActive = (planId) => {
    const currentPlanId = getCurrentPlanId();
    if (!currentPlanId) return false;
    
    return currentPlanId === planId && subscriptionUtils.isSubscriptionActive(currentSubscription);
  };

  const getButtonText = (plan) => {
    if (isPlanActive(plan.id)) {
      return 'Plan actuel';
    }

    if (plan.isFree) {
      if (hasUsedFreeTrial) {
        return 'Essai déjà utilisé';
      }
      return 'Commencer l\'essai gratuit';
    }

    if (plan.isOffered) {
      return 'Activer l\'accès offert';
    }

    return 'Choisir ce plan';
  };

  const getButtonVariant = (plan) => {
    if (isPlanActive(plan.id)) {
      return 'outline';
    }
    
    if (plan.popular) {
      return 'primary';
    }
    
    return 'secondary';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mr-3"></div>
        <span className="text-gray-600 dark:text-gray-400">Chargement des plans...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-6 mb-6">
        <div className="flex items-center">
          <Star className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2" />
          <p className="text-yellow-700 dark:text-yellow-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {plans.map((plan) => (
        <div
          key={plan.id}
          className={`relative rounded-lg border-2 p-6 transition-all duration-200 hover:shadow-lg ${
            plan.isOffered
              ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
              : plan.popular
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : isPlanActive(plan.id)
              ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
              : 'border-gray-200 bg-white dark:bg-gray-800'
          }`}
        >
          {/* Badge populaire ou offert */}
          {plan.isOffered && (
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <div className="bg-purple-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center">
                <Star className="w-4 h-4 mr-1" />
                Offert
              </div>
            </div>
          )}
          {plan.popular && !plan.isOffered && (
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center">
                <Star className="w-4 h-4 mr-1" />
                Populaire
              </div>
            </div>
          )}

          {/* Badge plan actuel */}
          {isPlanActive(plan.id) && (
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center">
                <CheckCircle className="w-4 h-4 mr-1" />
                Actuel
              </div>
            </div>
          )}

          {/* En-tête du plan */}
          <div className="text-center mb-6">
            {/* Tagline */}
            {plan.tagline && (
              <div className="mb-2">
                <span className={`text-xs font-semibold uppercase tracking-wider ${
                  plan.isOffered
                    ? 'text-purple-600 dark:text-purple-400'
                    : plan.popular
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400'
                }`}>
                  {plan.tagline}
                </span>
              </div>
            )}

            <div className="flex items-center justify-center mb-2">
              {plan.popular && <Zap className="w-5 h-5 text-blue-500 mr-2" />}
              <h3 className={`text-2xl font-bold ${
                plan.isOffered
                  ? 'text-purple-700 dark:text-purple-300'
                  : plan.popular
                  ? 'text-blue-700 dark:text-blue-300'
                  : isPlanActive(plan.id)
                  ? 'text-green-700 dark:text-green-300'
                  : 'text-gray-900 dark:text-white'
              }`}>
                {plan.name}
              </h3>
            </div>

            <div className="mb-3">
              <span className="text-4xl font-bold text-gray-900 dark:text-white">
                {subscriptionUtils.formatPrice(plan.price)}
              </span>
              {!plan.isFree && (
                <span className="text-gray-600 dark:text-gray-400 text-lg">/{plan.period}</span>
              )}
            </div>

            {/* Badge personnalisé */}
            {plan.badge && !isPlanActive(plan.id) && (
              <div className="mb-2">
                <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${
                  plan.isOffered
                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                    : plan.popular
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                }`}>
                  {plan.badge}
                </span>
              </div>
            )}

            {/* Économies */}
            {plan.savings && (
              <div className="mb-2">
                <span className="text-sm text-green-600 dark:text-green-400 font-semibold">
                  💰 {plan.savings}
                </span>
              </div>
            )}

            <p className="text-gray-600 dark:text-gray-400 text-sm mt-2 leading-relaxed">
              {plan.description}
            </p>
          </div>

          {/* Liste des fonctionnalités */}
          <ul className="space-y-3 mb-6">
            {plan.features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300 text-sm">{feature}</span>
              </li>
            ))}
          </ul>

          {/* Bouton d'action */}
          <button
            onClick={() => onPlanSelect(plan)}
            disabled={isPlanActive(plan.id) || (plan.isFree && hasUsedFreeTrial)}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
              plan.isOffered
                ? 'bg-purple-600 hover:bg-purple-700 text-white disabled:bg-purple-300'
                : getButtonVariant(plan) === 'primary'
                ? 'bg-blue-600 hover:bg-blue-700 text-white disabled:bg-blue-300'
                : getButtonVariant(plan) === 'outline'
                ? 'bg-green-100 hover:bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200 border border-green-300'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600'
            } disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md transition-shadow`}
          >
            <div className="flex items-center justify-center">
              {getButtonVariant(plan) === 'primary' && <CreditCard className="w-4 h-4 mr-2" />}
              {getButtonText(plan)}
            </div>
          </button>

          {/* Message si l'essai gratuit a déjà été utilisé */}
          {plan.isFree && hasUsedFreeTrial && (
            <div className="mt-3 text-center">
              <p className="text-xs text-orange-600 dark:text-orange-400">
                Vous avez déjà bénéficié de votre période d'essai gratuite
              </p>
            </div>
          )}

          {/* Prix mensuel équivalent pour le plan annuel */}
          {plan.period === 'an' && plan.price > 0 && (
            <div className="mt-3 text-center">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Soit {subscriptionUtils.formatPrice(Math.round(plan.price / 12))}/mois seulement
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default StripeSubscriptionPlans;
